import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  HomeIcon,
  UsersIcon,
  CalendarIcon,
  ClipboardDocumentCheckIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CogIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { getTaskCount } from '../../services/taskService';
import useSidebarStore from '../../store/sidebarStore';

const navGroups = [
  {
    label: null,
    items: [
      { name: 'Dashboard',    to: '/',            icon: HomeIcon },
      { name: 'Clients',      to: '/clients',     icon: UsersIcon },
      { name: 'Appointments', to: '/appointments', icon: CalendarIcon },
    ],
  },
  {
    label: 'Clinical',
    items: [
      { name: 'Notes',     to: '/notes',     icon: PencilSquareIcon },
      { name: 'Tasks',     to: '/tasks',     icon: ClipboardDocumentCheckIcon, badge: 'tasks' },
      { name: 'Messages',  to: '/messages',  icon: ChatBubbleLeftRightIcon },
    ],
  },
  {
    label: 'Financial',
    items: [
      { name: 'Billing',   to: '/billing',   icon: CreditCardIcon },
      { name: 'Insurance', to: '/insurance', icon: ShieldCheckIcon },
    ],
  },
  {
    label: 'Admin',
    items: [
      { name: 'Reports',      to: '/reports',      icon: ChartBarIcon },
      { name: 'Intake Forms', to: '/intake-forms', icon: ClipboardDocumentListIcon },
      { name: 'Settings',     to: '/settings',     icon: CogIcon },
    ],
  },
];

export default function Sidebar({ open, onClose }) {
  const location = useLocation();
  const { collapsed, toggle } = useSidebarStore();

  const { data: countData } = useQuery({
    queryKey: ['taskCount'],
    queryFn: getTaskCount,
    refetchInterval: 60_000,
  });
  const pendingCount = countData?.count ?? 0;

  const isActive = (to) =>
    location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-neutral-950/60 backdrop-blur-sm md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed z-30 inset-y-0 left-0 bg-white dark:bg-neutral-900
          border-r border-neutral-200 dark:border-neutral-700
          flex flex-col
          transition-[width,transform] duration-200
          md:static md:translate-x-0
          ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${collapsed ? 'w-14' : 'w-56'}
        `}
        aria-label="Main navigation"
      >
        {/* Header */}
        <div className={`flex items-center border-b border-neutral-200 dark:border-neutral-700 h-14 shrink-0
          ${collapsed ? 'justify-center px-0' : 'justify-between px-4'}`}
        >
          {!collapsed && (
            <span className="text-lg font-bold text-primary-600 tracking-tight">Sage</span>
          )}
          {/* Mobile close */}
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon md:hidden"
            aria-label="Close menu"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          {/* Desktop collapse toggle */}
          <button
            onClick={toggle}
            className="hidden md:flex btn btn-ghost btn-icon text-neutral-400"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed
              ? <ChevronDoubleRightIcon className="w-4 h-4" />
              : <ChevronDoubleLeftIcon className="w-4 h-4" />
            }
          </button>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
          {navGroups.map((group) => (
            <div key={group.label ?? 'main'}>
              {group.label && !collapsed && (
                <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-neutral-400">
                  {group.label}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.name}
                      to={item.to}
                      onClick={onClose}
                      title={collapsed ? item.name : undefined}
                      className={`
                        flex items-center rounded-lg transition-colors group
                        ${collapsed ? 'justify-center p-2' : 'px-3 py-2 gap-3'}
                        ${active
                          ? 'bg-neutral-100 dark:bg-neutral-800 text-primary-600 dark:text-primary-400 border-l-2 border-primary-600 dark:border-primary-400 rounded-l-none'
                          : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100'
                        }
                      `}
                    >
                      <item.icon className="w-5 h-5 shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-sm font-medium">{item.name}</span>
                          {item.badge === 'tasks' && pendingCount > 0 && (
                            <span className="bg-primary-600 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                              {pendingCount > 99 ? '99+' : pendingCount}
                            </span>
                          )}
                        </>
                      )}
                      {collapsed && item.badge === 'tasks' && pendingCount > 0 && (
                        <span className="absolute top-0 right-0 w-2 h-2 bg-primary-600 rounded-full" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
